import { useEffect, useState } from "react";
import { projects as staticProjects } from "../data/profile";

/**
 * projectsStore
 *
 * Projects live in MongoDB and are edited from the admin panel, but the site
 * also ships with a static list in profile.js. That list is not dead weight:
 *
 *   - it renders instantly, before any network round trip
 *   - it is what a crawler with no JS-fetch budget sees
 *   - it is the fallback if the DB is empty, slow, or down
 *
 * So the DB is an *override*, never a dependency. A cold database means the
 * portfolio still shows seven projects; it does not mean an empty page.
 */

const API = "/api/projects";

/** Shape every source into one object the UI can render without branching. */
export function normalise(p, i = 0) {
  return {
    id: p.key || p.id || p._id,
    _id: p._id || null,
    index: p.index || String(i + 1).padStart(2, "0"),
    year: p.year || "",
    title: p.title || "Untitled",
    tagline: p.tagline || "",
    category: p.category || "",
    client: p.client || "",
    role: p.role || "",
    liveUrl: p.liveUrl || "",
    repoUrl: p.repoUrl || "",
    accent: p.accent || "emerald",
    image: p.image || "",
    // Only the bundled projects carry imported video assets.
    mediaKey: p.mediaKey || null,
    posterKey: p.posterKey || null,
    summary: p.summary || "",
    problem: p.problem || "",
    approach: p.approach || "",
    result: p.result || "",
    highlights: Array.isArray(p.highlights) ? p.highlights : [],
    stack: Array.isArray(p.stack) ? p.stack : [],
    metrics: Array.isArray(p.metrics) ? p.metrics : [],
  };
}

/** The bundled list, already normalised. Used as the initial render. */
export const fallbackProjects = staticProjects.map(normalise);

/** Convert the bundled list into the payload POST /api/projects/seed wants. */
export function seedPayload() {
  return staticProjects.map((p, i) => ({
    key: p.id,
    title: p.title,
    tagline: p.tagline,
    category: p.category,
    client: p.client,
    role: p.role,
    year: p.year,
    liveUrl: p.liveUrl || "",
    repoUrl: p.repoUrl || "",
    image: p.image || "",
    accent: p.accent,
    summary: p.summary,
    problem: p.problem,
    approach: p.approach,
    result: p.result,
    highlights: p.highlights,
    stack: p.stack,
    metrics: p.metrics,
    order: (i + 1) * 10,
    featured: true,
    published: true,
  }));
}

/**
 * Read projects for the public site.
 *
 * Starts from the bundled list so there is content on first paint, then
 * swaps in the DB copy if there is one. A failed request is not an error
 * state here — it just means the fallback stands.
 */
export function useProjects() {
  const [projects, setProjects] = useState(fallbackProjects);
  const [source, setSource] = useState("static");

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    fetch(API, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!alive || !Array.isArray(data) || data.length === 0) return;
        setProjects(data.map(normalise));
        setSource("db");
      })
      .catch(() => {
        /* offline, 500, or no DB — the bundled list is already rendered */
      });

    return () => {
      alive = false;
      controller.abort();
    };
  }, []);

  return { projects, source };
}

/* ---------------- admin-side helpers ---------------- */

export async function fetchAllProjects() {
  const r = await fetch(`${API}?all=1`);
  if (!r.ok) throw new Error("Could not load projects");
  return r.json();
}

export async function createProject(body) {
  const r = await fetch(API, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error || "Create failed");
  return data;
}

export async function updateProject(id, body) {
  const r = await fetch(`${API}/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error || "Update failed");
  return data;
}

export async function deleteProject(id) {
  const r = await fetch(`${API}/${id}`, { method: "DELETE" });
  if (!r.ok) throw new Error("Delete failed");
  return r.json();
}

export async function reorderProjects(items) {
  const r = await fetch(`${API}/reorder/bulk`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });
  if (!r.ok) throw new Error("Reorder failed");
  return r.json();
}

export async function seedProjects() {
  const r = await fetch(`${API}/seed`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items: seedPayload() }),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error || "Seed failed");
  return data;
}
